import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutGrid, BookOpen, ShieldCheck, BarChart3, Users, Trophy,
  LogIn, User, LineChart, PanelLeftClose, PanelLeftOpen, Sparkles, Newspaper, Crown,
  Radar, Medal, Gift,
} from "lucide-react";
import { navItems, brand } from "../mock";

const iconMap = { LayoutGrid, BookOpen, LineChart, ShieldCheck, BarChart3, Users, Trophy, Sparkles, Newspaper, Crown, Medal, Radar, Gift };

const Sidebar = ({ user, onSignIn, onSignOut, collapsed = false, onToggle, mobileOpen = false, onMobileClose }) => {
  return (
    <aside className={`ws-sidebar fixed left-0 top-0 h-screen w-[240px] ${collapsed ? "lg:w-[68px]" : "lg:w-[240px]"} flex flex-col z-40 transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
      {/* Brand + collapse toggle */}
      <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} px-4 py-5`}>
        <span className="ws-logo-3d w-10 h-10" data-testid="sidebar-logo">
          <img src="/wsj-logo.webp" alt="Wealth Secret Journal" />
        </span>
        {!collapsed && (
          <div className="leading-tight flex-1 min-w-0">
            <div className="ws-heading text-white font-semibold text-[15px] truncate">{brand.name}</div>
            <div className="text-[9px] tracking-[0.18em] text-[color:var(--ws-muted)] font-medium truncate">
              DATA TERMINAL
            </div>
          </div>
        )}
        {!collapsed && (
          <button onClick={onToggle} title="Collapse sidebar" data-testid="sidebar-collapse-btn"
            className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <button onClick={onToggle} title="Expand sidebar" data-testid="sidebar-collapse-btn"
          className="hidden lg:flex mx-auto mb-1 w-8 h-8 rounded-lg items-center justify-center text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors">
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      <div className="ws-divider mx-4 mb-2" />

      {/* Nav */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => {
          const Icon = iconMap[item.icon];
          const isCoach = item.key === "ws-ai-coach";
          const isVip = item.vip;
          return (
            <NavLink
              key={item.key}
              data-testid={`nav-${item.key}`}
              to={item.path}
              end={item.path === "/"}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) => `ws-nav-link ${collapsed ? "justify-center" : ""} ${isActive ? "active" : ""} ${isCoach ? "ws-nav-coach" : ""} ${isVip ? "ws-nav-vip" : ""}`}
            >
              {isCoach ? (
                <span className="ws-coach-tile"><Icon className="w-[18px] h-[18px]" strokeWidth={2.2} /></span>
              ) : isVip ? (
                <span className="ws-vip-tile"><Icon className="w-[18px] h-[18px]" strokeWidth={2.2} /></span>
              ) : (
                <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={2} />
              )}
              {!collapsed && (
                isCoach ? (
                  <span className="text-[14px] font-semibold">
                    {item.label.split(/(AI)/).map((part, idx) => part === "AI"
                      ? <span key={idx} className="ws-ai-word">AI</span>
                      : <span key={idx}>{part}</span>)}
                  </span>
                ) : isVip ? (
                  <span className="text-[14px] font-semibold ws-vip-word">{item.label}</span>
                ) : item.soon ? (
                  <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className="text-[14px] whitespace-nowrap">{item.label}</span>
                    <span className="ws-soon-tag">Soon</span>
                  </span>
                ) : (
                  <span className="text-[14px]">{item.label}</span>
                )
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="ws-divider mx-4 my-2" />

      {/* User footer */}
      <div className={`px-4 py-4 ${collapsed ? "flex justify-center" : ""}`}>
        {user ? (
          collapsed ? (
            user.picture ? (
              <img src={user.picture} alt={user.name} title={user.name} className="w-9 h-9 rounded-full border border-[color:var(--ws-border-strong)]" />
            ) : (
              <button onClick={onSignOut} title={`${user.name} — Sign out`} className="w-9 h-9 rounded-full bg-[rgba(59,130,246,0.2)] border border-[color:var(--ws-border-strong)] flex items-center justify-center text-sm font-semibold text-[color:var(--ws-blue-bright)]">
                {user.name?.[0]?.toUpperCase() || "U"}
              </button>
            )
          ) : (
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-[color:var(--ws-border-strong)]" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[rgba(59,130,246,0.2)] border border-[color:var(--ws-border-strong)] flex items-center justify-center text-sm font-semibold text-[color:var(--ws-blue-bright)]">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white truncate">{user.name}</div>
              <button onClick={onSignOut} className="text-[11px] text-[color:var(--ws-muted)] hover:text-[color:var(--ws-blue-bright)] transition-colors">
                Sign out
              </button>
            </div>
          </div>
          )
        ) : (
          <button onClick={onSignIn} title="Sign in" className={`flex items-center ${collapsed ? "" : "w-full gap-3"} group`}>
            <div className="w-9 h-9 rounded-full bg-[rgba(59,130,246,0.12)] border border-[color:var(--ws-border)] flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-[color:var(--ws-muted)]" />
            </div>
            {!collapsed && (
            <div className="text-left">
              <div className="text-[13px] font-medium text-white flex items-center gap-1.5">
                Sign in <LogIn className="w-3.5 h-3.5 text-[color:var(--ws-blue-bright)]" />
              </div>
              <div className="text-[11px] text-[color:var(--ws-muted)]">Guest</div>
            </div>
            )}
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
